import React, { useState, useEffect, useRef, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import Layout from '../../components/Layout';
import ConsentForm from './ConsentForm';

import localStorage from 'localStorage';
import set from 'lodash/set';
import get from 'lodash/get';
import request from '../../utils/request';
import { withConsentLogoCacheBust } from '../../utils/consentLogo';
import {
  finishHtmlConsentBoot,
  hasHtmlConsentBoot,
  isHtmlConsentGiven,
} from '../../utils/consentBoot';
import { readConsentMailings } from '../../utils/consentMailings';
import { getCookie, deleteCookie } from '../../utils/cookies';
import {
  formatRequestError,
  isNetworkError,
} from '../../utils/requestErrorMessage';
import {
  fetchWalletCardWithRetry,
  issueWalletCardWithRetry,
  pickCardPayload,
} from '../../utils/walletCardIssue';
import { resolveLoginStep } from './Login.step';
import {
  cardSerialFromPath,
  clearStoredWalletPrefill,
  googleWalletTargetUrl,
  mapWalletPrefill,
  readStoredWalletPrefill,
  writeStoredWalletPrefill,
} from './googleWalletEnrollment';

const ResponseCode = {
  USER_EXISTS: 50002,
  CARD_EXISTS: 50003,
  NEED_TO_REGISTER: 403,
  TOKEN_NOT_VALID: 401
};

const getCardUrl = url => {
  if (!url) {
    return null;
  }
  const token = localStorage.getItem('access_token');
  if (!token) {
    return url;
  }
  const sep = String(url).indexOf('?') >= 0 ? '&' : '?';
  return `${url}${sep}jwt=${token}`;
};

const EXCLUDED_URL = 'develop.wollzy.com';
const OLD_REGISTER_METHOD = '/client/register';
const REGISTER_START = '/client/register::start';
const REGISTER_CHECK = '/client/register::check';
const REGISTER_FINISH = '/client/register::finish';

const importLoginForm = () => import('./LoginForm');
const importCallMe = () => import('./CallMe');
const importCardCreateForm = () => import('./CardCreateForm');
const importCard = () => import('./Card');
const importCertificateActivate = () => import('./CertificateActivate');

// Warm phone step while HTML consent is still on screen.
importLoginForm();

const deferredModuleCache = new Map();
const deferredPromiseCache = new Map();

function loadDeferredModule(importer) {
  const cached = deferredModuleCache.get(importer);
  if (cached) {
    return Promise.resolve(cached);
  }
  let pending = deferredPromiseCache.get(importer);
  if (!pending) {
    pending = importer().then(loaded => {
      const mod = loaded.default;
      deferredModuleCache.set(importer, mod);
      return mod;
    });
    deferredPromiseCache.set(importer, pending);
  }
  return pending;
}

function useDeferredModule(shouldLoad, importer) {
  const [module, setModule] = useState(
    () => deferredModuleCache.get(importer) || null,
  );

  useEffect(() => {
    if (!shouldLoad) {
      return undefined;
    }
    let cancelled = false;
    loadDeferredModule(importer).then(mod => {
      if (!cancelled) {
        setModule(() => mod);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, importer]);

  return module;
}

function StepLoading() {
  return (
    <div
      className='login-flow'
      style={{ padding: '48px', textAlign: 'center' }}
    >
      Загрузка…
    </div>
  );
}

function Login({ match, location, history }) {
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState(null);
  const [checkToken, setCheckToken] = useState(null);
  const [toCallPhone, setToCallPhone] = useState(null);
  const consentAlreadyGiven = isHtmlConsentGiven();
  const [consentGiven, setConsentGiven] = useState(consentAlreadyGiven);
  const [showLogin, setShowLogin] = useState(consentAlreadyGiven);
  // const [showConfirm, setShowConfirm] = useState(false);
  const [showCallMe, setShowCallMe] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [cardIsCreated, setCardCreated] = useState(false);
  const [googleAuth, setGoogleAuth] = useState(false);
  const [googleJwt, setGoogleJwt] = useState(null);
  const [walletPrefill, setWalletPrefill] = useState(() =>
    readStoredWalletPrefill(match.params.alias),
  );
  const walletSaveRedirectRef = useRef(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [questionnaireRules, setQuestionnaireRules] = useState(null);
  const [questionnaireRulesLoading, setQuestionnaireRulesLoading] =
    useState(false);
  const checkIntervalRef = useRef(null);
  const registerCheckInFlightRef = useRef(false);
  const sendPhoneInFlightRef = useRef(false);
  const walletIssueInFlightRef = useRef(false);
  const registerSubmitInFlightRef = useRef(false);
  const cardUrlRef = useRef(null);
  const [registerSubmitting, setRegisterSubmitting] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState(null);
  const [showCertificateActivate, setShowCertificateActivate] = useState(false);
  const [certificateActivation, setCertificateActivation] = useState(null);
  const [consentLogoUrl, setConsentLogoUrl] = useState('');
  const htmlConsentBoot = hasHtmlConsentBoot();

  useEffect(() => {
    if (isHtmlConsentGiven()) {
      setConsentGiven(true);
      setShowLogin(true);
      finishHtmlConsentBoot();
    }
    const onConsent = () => {
      setConsentGiven(true);
      setShowLogin(true);
      finishHtmlConsentBoot();
    };
    window.addEventListener('wollzy:consent', onConsent);
    return () => {
      window.removeEventListener('wollzy:consent', onConsent);
    };
  }, []);

  // Prefetch next steps while the user is still reading consent / phone.
  useEffect(() => {
    loadDeferredModule(importLoginForm);
  }, []);

  useEffect(() => {
    if (consentGiven || showLogin) {
      loadDeferredModule(importCallMe);
    }
  }, [consentGiven, showLogin]);

  useEffect(() => {
    if (showCallMe || showRegister || showCard) {
      loadDeferredModule(importCardCreateForm);
      loadDeferredModule(importCard);
    }
  }, [showCallMe, showRegister, showCard]);

  const LoginForm = useDeferredModule(true, importLoginForm);
  const CallMe = useDeferredModule(
    showLogin || showCallMe || consentGiven,
    importCallMe,
  );
  const CardCreateForm = useDeferredModule(
    showCallMe || showRegister,
    importCardCreateForm,
  );
  const Card = useDeferredModule(
    showCallMe || showRegister || showCard,
    importCard,
  );
  const CertificateActivate = useDeferredModule(
    consentGiven &&
      showCertificateActivate &&
      Boolean(match.params.certificateId),
    importCertificateActivate,
  );

  const handleConsent = useCallback(() => {
    finishHtmlConsentBoot();
    setConsentGiven(true);
    setShowLogin(true);
  }, []);

  useEffect(() => {
    const alias = match.params.alias;
    if (!alias) {
      setConsentLogoUrl('');
      return undefined;
    }
    let cancelled = false;

    const applyBranding = data => {
      if (cancelled) {
        return;
      }
      setConsentLogoUrl(
        withConsentLogoCacheBust((data && data.consent_logo_url) || ''),
      );
    };

    const early =
      typeof window !== 'undefined' &&
      window.__WOLLZY_BRANDING_ALIAS__ === alias
        ? window.__WOLLZY_BRANDING_PROMISE__
        : null;

    if (early) {
      early.then(applyBranding).catch(() => {
        if (!cancelled) {
          setConsentLogoUrl('');
        }
      });
      return () => {
        cancelled = true;
      };
    }

    request(`/partner/public/registration-branding/${alias}`)
      .then(applyBranding)
      .catch(() => {
        if (!cancelled) {
          setConsentLogoUrl('');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [match.params.alias]);

  useEffect(() => {
    const alias = match.params.alias;
    if (alias && match.params.serialNumber === 'google-wallet') {
      history.replace('/p/' + encodeURIComponent(alias));
    }
  }, [history, match.params.alias, match.params.serialNumber]);

  useEffect(() => {
    setWalletPrefill(readStoredWalletPrefill(match.params.alias));
  }, [match.params.alias]);

  useEffect(() => {
    const alias = match.params.alias;
    if (!alias) {
      return undefined;
    }
    let cancelled = false;
    request('/p/' + encodeURIComponent(alias) + '/google-wallet/prefill', {
      credentials: 'include',
    })
      .then((profile) => {
        if (cancelled) {
          return;
        }
        const mapped = mapWalletPrefill(profile);
        if (!mapped.fromWallet) {
          return;
        }
        writeStoredWalletPrefill(alias, mapped);
        setWalletPrefill(mapped);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [match.params.alias]);

  // Используем useCallback для меморизации функций
  const handleError401 = useCallback(() => {
    localStorage.removeItem('access_token');
    setShowCallMe(false);
    setShowLogin(true);
    setShowRegister(false);
    setShowCard(false);
  }, []);

  const handleFetch = useCallback((url, body, method = 'POST', headers = {}) => {
    return request(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: JSON.stringify(body)
    });
  }, []);

  // const handleSendCode = useCallback((phone, code) => {
  //   handleFetch(OLD_REGISTER_METHOD, { phone, 'secret_code': code, alias: match.params.alias})
  //     .then(data => {
  //       localStorage.setItem('access_token', data['access_token']);
  //       setError(null);
  //       setShowConfirm(false);
  //       setShowLogin(false);
  //
  //       if (data.code === ResponseCode.USER_EXISTS) {
  //         setCardCreated(true);
  //         setShowCard(true);
  //       } else {
  //         setShowRegister(true);
  //       }
  //     })
  //     .catch(err => setError(get(err, 'body.message')));
  // }, [
  //     handleFetch, match.params.alias, setCardCreated, setError, setShowCard, setShowConfirm, setShowLogin,
  //     setShowRegister,
  // ]);

  useEffect(() => {
    document.title = 'Аутентификация';

    if (window.location.host === EXCLUDED_URL) {
      localStorage.removeItem('access_token');
    }

    const authToken = getCookie('Authorization');

    if (authToken) {
      setGoogleAuth(authToken.substring(1, authToken.length - 1));
    }

    return () => {
      deleteCookie('Authorization');
      // Cleanup interval on unmount
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  // After consents always require phone authentication (card issue and certificate).
  // Do not reuse a stale access_token / Google cookie to skip LoginForm + CallMe.
  useEffect(() => {
    if (!consentGiven) {
      return;
    }

    setShowCertificateActivate(false);
    setShowRegister(false);
    setShowCard(false);
    setCardCreated(false);
    setShowCallMe(false);
    setShowLogin(true);
  }, [consentGiven]);

  useEffect(() => {
    if (!showRegister || !match.params.alias) {
      return undefined;
    }
    let cancelled = false;
    setQuestionnaireRulesLoading(true);
    request(
      `/partner/public/registration-questionnaire/${match.params.alias}`,
    )
      .then((data) => {
        if (!cancelled) {
          setQuestionnaireRules(data.registration_questionnaire_rules);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setQuestionnaireRules(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setQuestionnaireRulesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showRegister, match.params.alias]);

  const certificateId = match.params.certificateId;

  useEffect(() => {
    if (!certificateId) {
      setCertificateInfo(null);
      return undefined;
    }
    let cancelled = false;
    request(
      `/partner/public/loyalty-certificates/${encodeURIComponent(certificateId)}/claim-info`,
    )
      .then((res) => {
        if (cancelled || !res) {
          return;
        }
        if (res.program_alias && res.program_alias !== match.params.alias) {
          history.replace(
            `/p/${encodeURIComponent(res.program_alias)}/certificate/${encodeURIComponent(certificateId)}`,
          );
          return;
        }
        setCertificateInfo(res);
      })
      .catch(() => {
        if (!cancelled) {
          setCertificateInfo({ error: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [certificateId, match.params.alias, history]);

  const clearRegisterPoll = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  const authHeader = useCallback(
    (token) =>
      googleAuth ||
      `Bearer ${token || localStorage.getItem('access_token') || ''}`,
    [googleAuth],
  );

  const applyIssuedCard = useCallback((card) => {
    if (!card || !card.url) {
      return false;
    }
    const nextUrl = getCardUrl(card.url);
    cardUrlRef.current = nextUrl;
    setGoogleJwt(card.jwt);
    setCardUrl(nextUrl);
    setError(null);
    return true;
  }, []);

  useEffect(() => {
    const url = googleWalletTargetUrl(
      googleJwt,
      walletPrefill.fromWallet,
    );
    if (!url || walletSaveRedirectRef.current) {
      return;
    }
    walletSaveRedirectRef.current = true;
    clearStoredWalletPrefill(match.params.alias);
    window.location.href = url;
  }, [
    googleJwt,
    match.params.alias,
    walletPrefill.fromWallet,
  ]);

  const fetchClientCards = useCallback(
    (token) =>
      request('/client/clients/cards?alias=' + match.params.alias, {
        method: 'GET',
        headers: {
          Authorization: authHeader(token),
        },
      }),
    [authHeader, match.params.alias],
  );

  const postClientCard = useCallback(
    (token) => {
      const body = {
        alias: match.params.alias,
      };

      const serial = cardSerialFromPath(match.params.serialNumber);
      if (serial) {
        set(body, 'serialnumber', serial);
      }

      return request('/client/clients/cards' + (location.search || ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeader(token),
        },
        body: JSON.stringify(body),
      });
    },
    [authHeader, location.search, match.params.alias, match.params.serialNumber],
  );

  const handleGetCard = useCallback(
    (token) => {
      if (cardUrlRef.current || walletIssueInFlightRef.current) {
        return;
      }
      walletIssueInFlightRef.current = true;
      setError(null);

      fetchWalletCardWithRetry({
        fetchCard: () => fetchClientCards(token),
      })
        .then((data) => {
          const card = pickCardPayload(data);
          if (applyIssuedCard(card)) {
            walletIssueInFlightRef.current = false;
            return;
          }

          // No pass URL yet — create a new card for this partner
          walletIssueInFlightRef.current = false;
          setCardCreated(false);
        })
        .catch((err) => {
          walletIssueInFlightRef.current = false;
          if (err.status === ResponseCode.NEED_TO_REGISTER) {
            setShowCard(false);
            setShowLogin(false);
            setShowRegister(true);
          } else if (err.status === ResponseCode.TOKEN_NOT_VALID) {
            handleError401();
          } else {
            setError(
              formatRequestError(
                err,
                'Не удалось загрузить карту. Проверьте интернет и нажмите «Повторить».',
              ),
            );
          }
        });
    },
    [applyIssuedCard, fetchClientCards, handleError401],
  );

  const handleCreateCard = useCallback(
    (token) => {
      if (cardUrlRef.current || walletIssueInFlightRef.current) {
        return;
      }
      walletIssueInFlightRef.current = true;
      setError(null);

      issueWalletCardWithRetry({
        createCard: () => postClientCard(token),
        fetchCard: () => fetchClientCards(token),
        isCardExists: (err) => get(err, 'body.code') === ResponseCode.CARD_EXISTS,
      })
        .then((card) => {
          applyIssuedCard(card);
          walletIssueInFlightRef.current = false;
        })
        .catch((err) => {
          walletIssueInFlightRef.current = false;
          if (err.status === ResponseCode.TOKEN_NOT_VALID) {
            handleError401();
            return;
          }
          setError(
            formatRequestError(
              err,
              'Не удалось загрузить карту. Проверьте интернет и нажмите «Повторить».',
            ),
          );
        });
    },
    [
      applyIssuedCard,
      fetchClientCards,
      handleError401,
      postClientCard,
    ],
  );

  const handleRetryWalletCard = useCallback(() => {
    setError(null);
    cardUrlRef.current = null;
    walletIssueInFlightRef.current = false;
    handleCreateCard();
  }, [handleCreateCard]);

  // Start Apple/Google card issuance as early as possible (right after flag=true).
  const startWalletCard = useCallback(
    (token) => {
      setCardCreated(false);
      setShowCard(true);
      handleCreateCard(token);
    },
    [handleCreateCard],
  );

  const showWalletCardAfterCertificate = useCallback(() => {
    setShowCertificateActivate(false);
    setShowRegister(false);
    setShowLogin(false);
    setShowCallMe(false);
    startWalletCard();
  }, [startWalletCard]);

  const finishCertificateEnrollment = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return request('/client/certificate/claim/activate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: googleAuth || (token ? `Bearer ${token}` : ''),
      },
      body: JSON.stringify({
        alias: match.params.alias,
        certificate_id: Number(match.params.certificateId),
      }),
    })
      .then((res) => {
        setCertificateActivation({
          done: true,
          alreadyClaimed: Boolean(res && res.data && res.data.already_claimed),
          error: null,
        });
        showWalletCardAfterCertificate();
      })
      .catch((e) => {
        const msg =
          (e && e.body && (e.body.message || e.body.errors)) ||
          (e && e.json && (e.json.message || e.json.errors)) ||
          e.message ||
          'Не удалось активировать';
        setCertificateActivation({
          done: false,
          alreadyClaimed: false,
          error: typeof msg === 'string' ? msg : JSON.stringify(msg),
        });
        setShowCertificateActivate(true);
      });
  }, [
    googleAuth,
    match.params.alias,
    match.params.certificateId,
    showWalletCardAfterCertificate,
  ]);

  const handleRegisterCheck = useCallback((phone, checkToken) => {
    if (registerCheckInFlightRef.current) {
      return;
    }
    registerCheckInFlightRef.current = true;

    handleFetch(REGISTER_CHECK, {
      phone,
      check_token: checkToken,
      alias: match.params.alias,
    }, 'POST')
      .then(response => {
        // Проверяем условия: flag = true
        if (response.data.flag === true) {
          localStorage.setItem('access_token', response.data.access_token);

          setShowCallMe(false);
          setError(null);
          setShowLogin(false);

          const cid = match.params.certificateId;
          if (cid) {
            request(
              `/client/certificate/claim/routing?alias=${encodeURIComponent(match.params.alias)}&certificate_id=${encodeURIComponent(cid)}`,
              {
                headers: {
                  Authorization: `Bearer ${response.data.access_token}`,
                },
              },
            )
              .then((routing) => {
                clearRegisterPoll();
                const next = routing && routing.data && routing.data.next;
                if (next === 'activate') {
                  finishCertificateEnrollment();
                  return;
                }
                if (response.data.code === ResponseCode.USER_EXISTS) {
                  finishCertificateEnrollment();
                  return;
                }
                setShowRegister(true);
              })
              .catch(() => {
                clearRegisterPoll();
                if (response.data.code === ResponseCode.USER_EXISTS) {
                  finishCertificateEnrollment();
                } else {
                  setShowRegister(true);
                }
              });
            return;
          }

          if (response.data.code === ResponseCode.USER_EXISTS) {
            startWalletCard(response.data.access_token);
          } else {
            setShowRegister(true);
          }

          clearRegisterPoll();
        }
      })
      .catch(err => {
        console.error('Ошибка при проверке статуса регистрации:', err);
        if (err.status === ResponseCode.TOKEN_NOT_VALID) {
          handleError401();
          // Останавливаем интервал при ошибке авторизации
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      })
      .finally(() => {
        registerCheckInFlightRef.current = false;
      });
  }, [
    handleFetch,
    match.params.alias,
    match.params.certificateId,
    handleError401,
    clearRegisterPoll,
    finishCertificateEnrollment,
    startWalletCard,
  ]);

  // Poll register::check while CallMe is visible. iOS Safari freezes timers
  // while the Phone app is open after tel: — resume on visibility/pageshow/focus.
  useEffect(() => {
    if (showCallMe && phone && checkToken && toCallPhone) {
      const tick = () => {
        handleRegisterCheck(phone, checkToken);
      };

      tick();
      checkIntervalRef.current = setInterval(tick, 1500);

      const onResume = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          return;
        }
        tick();
      };

      document.addEventListener('visibilitychange', onResume);
      window.addEventListener('pageshow', onResume);
      window.addEventListener('focus', onResume);

      return () => {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        document.removeEventListener('visibilitychange', onResume);
        window.removeEventListener('pageshow', onResume);
        window.removeEventListener('focus', onResume);
      };
    }
  }, [showCallMe, phone, checkToken, toCallPhone, handleRegisterCheck]);

  const handleSendPhone = (phone) => {
    if (sendPhoneInFlightRef.current) {
      return;
    }
    sendPhoneInFlightRef.current = true;

    handleFetch(REGISTER_START, {phone, alias: match.params.alias})
      .then(data => {
        setPhone(phone);
        setCheckToken(data.data.checkToken);
        setToCallPhone(data.data.confirmationNumber);
        setShowCallMe(true);
        setShowLogin(false);
        setError(null);
    })
      .catch(err => {
        console.log(err.toString());
        setError(err.body && err.body.message);
        setShowLogin(true);
      })
      .finally(() => {
        sendPhoneInFlightRef.current = false;
      });
  };

  const handleSendUser = values => {
    if (registerSubmitInFlightRef.current) {
      return;
    }
    registerSubmitInFlightRef.current = true;
    setRegisterSubmitting(true);
    setError(null);

    if (phone) {
      set(values, 'phone', phone);
    }

    if (values.birthday) {
      // DD.MM.YYYY → YYYY-MM-DD without pulling moment into the consent bundle
      const parts = String(values.birthday).split('.');
      if (parts.length === 3) {
        set(values, 'birthday', `${parts[2]}-${parts[1]}-${parts[0]}`);
      }
    }

    const consentMailings = readConsentMailings();
    set(values, 'consent_sms', consentMailings);
    set(values, 'consent_email', consentMailings);

    const postRegisterFinish = () =>
      handleFetch(REGISTER_FINISH, values, 'POST', {
        Authorization:
          googleAuth || `Bearer ${localStorage.getItem('access_token')}`,
      });

    postRegisterFinish()
      .catch((err) => {
        if (isNetworkError(err)) {
          return new Promise((resolve) => setTimeout(resolve, 800)).then(
            postRegisterFinish,
          );
        }
        throw err;
      })
      .then(() => {
        setShowRegister(false);
        setRegisterSubmitting(false);
        registerSubmitInFlightRef.current = false;
        if (match.params.certificateId) {
          return finishCertificateEnrollment();
        }
        startWalletCard();
      })
      .catch((err) => {
        registerSubmitInFlightRef.current = false;
        setRegisterSubmitting(false);
        if (err.status === ResponseCode.TOKEN_NOT_VALID) {
          handleError401();
        }

        setError(
          formatRequestError(
            err,
            'Не удалось сохранить анкету. Проверьте интернет и нажмите «Далее» ещё раз.',
          ),
        );
      });
  };

  const loginStep = resolveLoginStep({
    consentGiven,
    htmlConsentBoot,
    showCertificateActivate,
    certificateId: match.params.certificateId,
    showCallMe,
    showRegister,
    showCard,
    showLogin,
  });

  return (
    <Layout
      className={`page login-page${
        !consentGiven || showRegister || showLogin
          ? ' login-page--consent'
          : ''
      }`}
    >
      {loginStep === 'consent' && (
        <ConsentForm
          alias={match.params.alias}
          consentLogoUrl={consentLogoUrl}
          onContinue={handleConsent}
        />
      )}

      {loginStep === 'certificate' &&
        (CertificateActivate ? (
          <CertificateActivate
            alias={match.params.alias}
            certificateId={match.params.certificateId}
            info={certificateInfo}
            initialDone={Boolean(
              certificateActivation && certificateActivation.done,
            )}
            initialAlreadyClaimed={Boolean(
              certificateActivation && certificateActivation.alreadyClaimed,
            )}
            initialError={
              certificateActivation && certificateActivation.error
                ? certificateActivation.error
                : null
            }
            onSuccess={showWalletCardAfterCertificate}
          />
        ) : (
          <StepLoading />
        ))}

      {loginStep === 'login' &&
        (LoginForm ? (
          <LoginForm
            onSendPhone={handleSendPhone}
            error={error}
            initialPhoneDigits={walletPrefill.phoneDigits}
          />
        ) : (
          <StepLoading />
        ))}

      {loginStep === 'callme' &&
        (CallMe ? (
          <CallMe toCallPhone={toCallPhone} enteredPhone={phone} />
        ) : (
          <StepLoading />
        ))}

      {loginStep === 'register' &&
        (CardCreateForm ? (
          <CardCreateForm
            error={error}
            googleAuth={googleAuth}
            handleSendUser={handleSendUser}
            questionnaireRules={questionnaireRules}
            questionnaireRulesLoading={questionnaireRulesLoading}
            submitting={registerSubmitting}
            initialValues={{
              firstName: walletPrefill.firstName,
              lastName: walletPrefill.lastName,
              email: walletPrefill.email,
              city: walletPrefill.city,
            }}
          />
        ) : (
          <StepLoading />
        ))}

      {loginStep === 'card' &&
        (Card ? (
          <Card
            error={error}
            cardIsCreated={cardIsCreated}
            handleCreateCard={handleCreateCard}
            handleGetCard={handleGetCard}
            handleRetry={handleRetryWalletCard}
            googleJwt={googleJwt}
            cardUrl={cardUrl}
          />
        ) : (
          <StepLoading />
        ))}
    </Layout>
  );
}

export default withRouter(Login);
